import { Grid, Link, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import ReactMarkdown from 'react-markdown'

import { Wallet } from '../../../types/wallet'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '950px',
    margin: 'auto',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  mainSection: {
    maxWidth: '90%',
    margin: '50px auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 50px',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      margin: '10px auto',
      padding: '0 25px',
    },
  },
  title: {
    fontSize: '2em',
    marginBottom: '15px',
    letterSpacing: 0,
    [theme.breakpoints.down('md')]: {
      fontSize: '28px',
    },
  },
  description: {
    background: '#fafafa',
    marginBottom: '20px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '20px',
    },
  },
  buttonContainer: {
    marginTop: '25px',
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
  },
  button: {
    height: '64px',
    width: '215px',
    fontSize: '18px',
    [theme.breakpoints.down('md')]: {
      margin: '30px auto',
    },
  },
  iconLike: {
    marginRight: '20px',
    height: '25px',
    width: '30px',
  },
  like: {
    marginTop: '20px',
    [theme.breakpoints.down('md')]: {
      marginTop: '10px',
    },
  },
  markdown: {
    fontSize: '18px',
    color: '#555555',
    lineHeight: '1.8em',
    overflowWrap: 'break-word',
    '& img': {
      maxWidth: '100%',
    },
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  likeTitle: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  titleBackground: {
    position: 'absolute',
    top: '15px',
    left: '10px',
    width: '90%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    zIndex: -1,
  },
  review: {
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      marginTop: 0,
    },
  },
  reviewTitle: {
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  reviewMarkdown: {
    color: '#585858',
  },
  purpleSquare2: {
    position: 'absolute',
    zIndex: -1,
    top: '-20px',
    left: '0',
    border: `2px solid ${theme.palette.primary.main}`,
    width: '120%',
    height: '100%',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  hideMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  showMobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  promotionSection: {
    position: 'relative',
    marginBottom: '20px',
    backgroundColor: '#4600ba87',
    padding: '1rem 2rem',
    borderRadius: '1.15rem',
    '& > h6': {
      color: 'white',
      fontSize: '1rem',
    },
  },
  promotion: {
    color: 'white',
    fontWeight: 'bold',
    position: 'relative',
  },
  bestThing: {
    backgroundColor: 'rgba(50, 186, 2, 0.1)',
    padding: '1rem 2rem',
    borderRadius: '1.15rem',
    '& > h6': {
      color: 'rgba(50, 186, 2, 1)',
      fontSize: '1rem',
    },
  },
  dislike: {
    margin: '1.2rem 0',
    backgroundColor: 'rgba(255, 196, 34, 0.1)',
    padding: '1rem 2rem',
    borderRadius: '1.15rem',
    '& > h6': {
      color: 'rgba(255, 196, 34, 1)',
      fontSize: '1rem',
    },
  },
}))

function WalletReview(props: Wallet): JSX.Element {
  const classes = useStyles({})
  const goToSite = () => window.open(props.url)

  return (
    <div className={classes.container}>
      <div className={classes.mainSection}>
        <Typography variant="h1" className={classes.title}>
          About {props.title}
        </Typography>

        {props.photos.length > 0 && (
          <Grid container>
            {props.photos.slice(0, 3).map((photo, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <img src={photo.url} alt={props.title} height={200} />
              </Grid>
            ))}
          </Grid>
        )}

        {props.like && (
          <div className={classes.bestThing}>
            <Typography variant="h6">What we like about {props.title}</Typography>
            <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
              {props.like}
            </ReactMarkdown>
          </div>
        )}

        {props.dislike && (
          <div className={classes.dislike}>
            <Typography variant="h6">What we dislike about {props.title}</Typography>
            <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
              {props.dislike}
            </ReactMarkdown>
          </div>
        )}

        <div>
          {props.promotion ? (
            props.promotion_url ? (
              <Link href={props.promotion_url} target="_blank">
                <div className={classes.promotionSection}>
                  <Typography variant="h6">Promotion</Typography>
                  <Typography variant="subtitle2" className={classes.promotion}>
                    {props.promotion}
                  </Typography>
                </div>
              </Link>
            ) : (
              <div className={classes.promotionSection}>
                <Typography variant="h6">Promotion</Typography>
                <Typography variant="subtitle2" className={classes.promotion}>
                  {props.promotion}
                </Typography>
              </div>
            )
          ) : null}

          {props.review && (
            <div className={classes.review}>
              <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
                {props.review}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WalletReview
